import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  CircularProgress,
  Paper,
  TableSortLabel,
  TablePagination,
  Alert
} from '@mui/material';
import {
  useBillingInvoicing,
  BillingItem as _BillingItem
} from '../context/BillingInvoicingContext';
import { createInvoice } from '../api/billingInvoicingApi';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatBillingItemDescription } from '../utils/descriptionFormatter';

const InvoiceCreation: React.FC = () => {
  const { t } = useTranslation();
  const {
    billingItems,
    setInvoice,
    setCurrentStep,
    selectedCompany,
    currentStep,
    selectedOrders
  } = useBillingInvoicing();
  const [selected, setSelected] = useState<string[]>(
    billingItems.filter((bi) => bi.status === 'APPROVED').map((bi) => bi.id)
  );
  const [invoiceDate, setInvoiceDate] = useState(() =>
    format(new Date(), 'yyyy-MM-dd')
  );
  const [dueDate, setDueDate] = useState(() =>
    format(new Date(), 'yyyy-MM-dd')
  );
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState<string>('id,asc');

  // For order number lookup
  const orderMap = selectedOrders.reduce((acc, order) => {
    acc[order.id] = order;
    return acc;
  }, {} as Record<string, any>);

  const handleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(paginatedItems.map((item) => item.id));
    } else {
      setSelected([]);
    }
  };

  const handleSort = (property: string) => {
    const [currentProp, currentDir] = sort.split(',');
    let direction = 'asc';
    if (currentProp === property && currentDir === 'asc') direction = 'desc';
    setSort(`${property},${direction}`);
  };

  // Sorting logic
  const sortedItems = [...billingItems].sort((a, b) => {
    const [property, direction] = sort.split(',');
    let aValue = a[property as keyof typeof a];
    let bValue = b[property as keyof typeof b];
    if (property === 'grossAmount') {
      aValue = a.grossAmount || 0;
      bValue = b.grossAmount || 0;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
  });

  // Pagination logic
  const paginatedItems = sortedItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const total = billingItems
    .filter((item) => selected.includes(item.id))
    .reduce((sum, item) => sum + (item.grossAmount || 0), 0);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      if (!selectedCompany) {
        setError(
          t('billing.invoice_creation.error_no_company', 'No company selected')
        );
        setCreating(false);
        return;
      }
      const invoice = await createInvoice(selectedCompany.id, {
        billingItemIds: selected,
        invoiceDate,
        dueDate,
        rtuId: selectedCompany.id
      });
      setInvoice(invoice);
      setCurrentStep('invoice-details');
    } catch {
      setError(
        t('billing.invoice_creation.error_create', 'Failed to create invoice')
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box mt={4}>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          'billing.invoice_creation.info_banner',
          'Only approved billing items are eligible for invoice creation. If you do not see an item, please ensure it is approved in the previous step.'
        )}
      </Alert>
      <Typography variant="h6" mb={2}>
        {t('billing.invoice_creation.title', 'Invoice Creation')}
      </Typography>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label={t('billing.invoice_creation.invoice_date', 'Invoice Date')}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={invoiceDate}
          onChange={(e) => setInvoiceDate(e.target.value)}
        />
        <TextField
          label={t('billing.invoice_creation.due_date', 'Due Date')}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </Box>
      <Paper
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <TableContainer
          sx={{ maxHeight: 'calc(100vh - 340px)', width: '100%', flex: 1 }}
        >
          <Table
            stickyHeader
            aria-label="Invoice Creation Table"
            sx={{ tableLayout: 'fixed', width: '100%' }}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '5%' }}>
                  <Checkbox
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < paginatedItems.length
                    }
                    checked={
                      paginatedItems.length > 0 &&
                      selected.length === paginatedItems.length
                    }
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': t('Select all billing items') }}
                  />
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  <TableSortLabel
                    active={sort.startsWith('orderId')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('orderId')}
                  >
                    {t(
                      'billing.billing_item_summary.table.order_number',
                      'Order Number'
                    )}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <TableSortLabel
                    active={sort.startsWith('id')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    {t('billing.invoice_creation.table.billing_item_id')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '28%' }}>
                  <TableSortLabel
                    active={sort.startsWith('description')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('description')}
                  >
                    {t('billing.invoice_creation.table.description')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <TableSortLabel
                    active={sort.startsWith('status')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    {t('billing.invoice_creation.table.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <TableSortLabel
                    active={sort.startsWith('grossAmount')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('grossAmount')}
                  >
                    {t('billing.invoice_creation.table.amount_gross')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item, idx) => {
                const order = orderMap[item.orderId];
                return (
                  <TableRow key={item.id} hover>
                    <TableCell padding="checkbox" sx={{ py: 2 }}>
                      <Checkbox
                        checked={selected.includes(item.id)}
                        onChange={() => handleSelect(item.id)}
                        inputProps={{
                          'aria-label': t('Select billing item') + ' ' + item.id
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {order?.requestReferenceId ||
                        order?.orderNumber ||
                        order?.id ||
                        '-'}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{item.id}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.description ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatBillingItemDescription(
                              item.description
                            )
                          }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          fontWeight: 500,
                          color: 'white',
                          backgroundColor:
                            item.status === 'CREATED'
                              ? 'info.main'
                              : item.status === 'UNDER_REVIEW'
                              ? 'warning.main'
                              : item.status === 'APPROVED'
                              ? 'success.main'
                              : item.status === 'REJECTED'
                              ? 'error.main'
                              : 'grey.500',
                          fontSize: 13
                        }}
                      >
                        {t(
                          `billing.billing_item_summary.status.${item.status.toLowerCase()}`,
                          item.status
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.grossAmount != null
                        ? `€${item.grossAmount.toFixed(2)}`
                        : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={5} align="right">
                  <strong>
                    {t('billing.invoice_creation.table.total', 'Total')}
                  </strong>
                </TableCell>
                <TableCell>
                  <strong>{`€${billingItems
                    .filter((item) => selected.includes(item.id))
                    .reduce((sum, item) => sum + (item.grossAmount || 0), 0)
                    .toFixed(2)}`}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={billingItems.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
          labelRowsPerPage={t('common.rows_per_page')}
        />
      </Paper>
      {error && (
        <Typography color="error" mt={2}>
          {error}
        </Typography>
      )}
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={selected.length === 0 || creating}
          onClick={handleCreate}
          title={
            selected.length === 0
              ? t(
                  'billing.invoice_creation.create_tooltip',
                  'You must select at least one approved billing item to create an invoice.'
                )
              : undefined
          }
        >
          {creating ? (
            <CircularProgress size={20} />
          ) : (
            t('billing.invoice_creation.create', 'Create Invoice')
          )}
        </Button>
        {selected.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 2, alignSelf: 'center' }}
          >
            {t(
              'billing.invoice_creation.create_inline',
              'You must select at least one approved billing item to create an invoice.'
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InvoiceCreation;
